// Canonical CSS variables, single source of truth across admin and embed.
//
// SEP-2026 — EL CHROME DEL ADMIN PASA AL SISTEMA BRANDEATE.
//
// El :root de abajo ES el sistema de diseño del ecosistema Brandeate
// (brandeate.es + la app + este panel): neutros «tinta», un naranja
// señal usado con cuentagotas, CTA en tinta, Geist, radios generosos
// y sombras difusas. Los valores son copia literal del tokens.css del
// ecosistema; sólo cambia el prefijo --dfp-, que se conserva porque lo
// consumen ~3.100 declaraciones (sidebar.ts, v2-editor, ui-primitives).
// Cada token lleva anotado su nombre en el sistema.
//
// LO QUE VE EL CLIENTE FINAL NO ENTRA EN ESTO, y no por descuido: la
// tienda vende una estética cálida y editorial (Positive Prints) que
// está validada y no tiene por qué parecerse al software con el que se
// fabrica. Las dos superficies de cliente se protegen SOLAS, cada una
// por su mecanismo:
//
//   · El EMBED redeclara en .dfp-root / .bd-overlay todos los tokens
//     visuales que consume —superficies, tinta, acento, cremas, radios,
//     curva y las tres familias tipográficas—, y esos scopes ganan al
//     :root. Sólo hay TRES tokens que usa sin redeclarar:
//     --dfp-panel (sigue siendo #FFFFFF, no se mueve), --dfp-border-soft
//     y --dfp-success-text, que quedan pinados en
//     apps/embed/src/styles/parts/00-base-root.ts justo para esto.
//     Si algún día el embed empieza a consumir un token nuevo del
//     :root, o lo redeclara allí o heredará el panel.
//   · El ESTUDIO 3D DEL CLIENTE (3d.html / 3d-personalizar.html) ni
//     siquiera llama a injectTokens(): monta el sistema de diseño del
//     embed y mapea los pocos --dfp-* que su CSS usa
//     (customer-3d/main.styles.ts).
//
// Estrategia de naming (importante):
//
//   1. Los nombres `--dfp-*` históricos SE MANTIENEN. Renombrarlos
//      sería destructivo y no aporta valor visual.
//
//   2. Hay DOS paletas de marca conviviendo y no son la misma:
//        - `--dfp-signal-*`  → naranja señal de Brandeate. El PANEL.
//        - `--dfp-brand-*`   → rojo/coral comercial. El PRODUCTO del
//                              cliente (embed + estudio 3D).
//      Un botón primario del admin no lleva ninguno de los dos: lleva
//      tinta (`--dfp-accent`).
//
//   3. Divergencia deliberada: la escala --dfp-space-* conserva su
//      numeración actual (1=2px … 8=32px), y las duraciones se quedan
//      en 80/150/250 en vez de las del sistema (180/250/450) — ver la
//      nota en el bloque de movimiento.
//
// One injection per document. Calling `injectTokens()` twice is a no-op.

const STYLE_ID = 'dfp-ui-tokens';

export const TOKENS_CSS = /* css */ `
:root {
  /* ═══════════════════════════════════════════════════════════
   * SISTEMA BRANDEATE (sep-2026) — el mismo de brandeate.es y de
   * la app. Los valores de abajo son copia literal de tokens.css
   * del ecosistema; lo único que cambia aquí es el prefijo
   * --dfp-, que se conserva porque hay ~3.100 consumidores.
   * La correspondencia va anotada token a token.
   *
   * ESTO ES EL CHROME DEL SOFTWARE, NO LA TIENDA. El embed
   * (.dfp-root / .bd-overlay) y el personalizador 3D del cliente
   * (3d-personalizar.html) redeclaran sus propios valores en su
   * scope: siguen en la estética comercial cálida validada
   * (Positive Prints). Ver la nota de más abajo sobre los tres
   * tokens que el embed NO redeclara.
   * ═══════════════════════════════════════════════════════════ */

  /* ─── 1. NEUTROS — «Tinta» (tokens.css §1, --ink-*) ─────────
   * Neutro ligeramente cálido. No negro puro: el negro puro vibra
   * en pantalla y abarata el conjunto.
   *
   * OJO: esta escala SUSTITUYE a la tinta cálida marrón del
   * handoff comercial (#14110C…#DCCFB6). Nadie del lado cliente
   * la consume — el embed no usa var(--dfp-ink-N) en ninguna
   * parte y ui-primitives, que sí, es exclusivo del admin. */
  --dfp-ink-950:          #121214;
  --dfp-ink-900:          #1A1A1D;
  --dfp-ink-800:          #26262A;
  --dfp-ink-700:          #3D3D42;
  --dfp-ink-600:          #55555C;
  --dfp-ink-500:          #6E6E76;
  --dfp-ink-400:          #909099;
  --dfp-ink-300:          #B4B4BC;
  --dfp-ink-200:          #D6D6DC;
  --dfp-ink-100:          #E8E8EC;
  --dfp-ink-50:           #F4F4F6;

  /* ─── 2. NARANJA SEÑAL (tokens.css §2, --brand-*) ───────────
   * El naranja de la señalética de almacén. Se usa POCO y en
   * sitios concretos: nunca como fondo de sección entera, nunca
   * como color del botón principal — el CTA es tinta.
   *
   * Es luminoso: sobre papel solo llega a 2,89:1. Para TEXTO usa
   * siempre el 700 (5,89:1, AA); para bordes y gráficos con
   * significado, el 600 (3,97:1).
   *
   * Se llama «signal» y no «brand» porque --dfp-brand-red y
   * compañía ya existen y son la paleta COMERCIAL (el rojo del
   * personalizador del cliente). Dos marcas, dos nombres. */
  --dfp-signal-50:        #FEF7F0;
  --dfp-signal-100:       #FCECDD;
  --dfp-signal-200:       #F8D3B2;
  --dfp-signal-300:       #F3B881;
  --dfp-signal-400:       #EE9747;
  --dfp-signal-500:       #E8760D;   /* color de marca */
  --dfp-signal-600:       #C3630B;   /* mínimo para gráficos con significado */
  --dfp-signal-700:       #994E09;   /* mínimo para texto */
  --dfp-signal-800:       #6F3906;

  /* ─── Brand comercial (handoff) — SIN CAMBIOS ──────────────
   * Es el rojo del PRODUCTO del cliente, no del panel. Lo
   * consume el embed (vía el espejo TS en tokens.ts) y la vista
   * de cliente del estudio 3D. El admin ya no lo usa: los
   * primitives pasaron a los alias semánticos en sep-2026. */
  --dfp-brand-red:        #C8362B;
  --dfp-brand-red-deep:   #A52A22;
  --dfp-brand-coral:      #ED7A5F;
  --dfp-brand-coral-soft: #F4C8B8;
  --dfp-brand-tint:       #FDECE9;

  /* ─── Cremas — re-apuntadas a la escala neutra ─────────────
   * Los nombres se conservan porque ui-primitives (admin) los
   * consume en ~40 sitios; el EMBED redeclara las cinco en su
   * propio scope, así que el papel crema del cliente no se mueve. */
  --dfp-cream-50:         var(--dfp-ink-50);    /* fondo de inputs */
  --dfp-cream-100:        var(--dfp-ink-50);    /* lienzo del preview */
  --dfp-cream-150:        var(--dfp-ink-100);   /* fondo de iconos */
  --dfp-cream-200:        var(--dfp-ink-100);   /* bordes sutiles */
  --dfp-cream-300:        var(--dfp-ink-200);

  /* ─── Paper / panel / canvas ──────────────────────────────── */
  --dfp-paper:            #FFFFFF;              /* tokens.css --white */
  --dfp-paper-warm:       #FBFBFC;              /* tokens.css --paper */
  --dfp-panel:            #FFFFFF;
  --dfp-canvas:           var(--dfp-ink-50);
  /* El guión del eyebrow: aquí SÍ va el naranja. Es un elemento
   * gráfico diminuto con significado, que es exactamente el uso
   * que tokens.css reserva para el 500. */
  --dfp-tick:             var(--dfp-signal-500);

  /* ─── 5. ROLES — lo que consumen los componentes ───────────── */

  /* Surfaces — papel casi blanco, tarjetas blancas */
  --dfp-bg:               #FBFBFC;              /* --paper */
  --dfp-surface:          #FFFFFF;              /* --white */
  --dfp-surface-2:        var(--dfp-ink-50);
  --dfp-surface-3:        var(--dfp-ink-100);
  --dfp-surface-hover:    var(--dfp-ink-50);

  /* Borders — hairlines neutras */
  --dfp-border:           var(--dfp-ink-100);   /* --border */
  --dfp-border-soft:      var(--dfp-ink-50);
  --dfp-border-strong:    var(--dfp-ink-200);   /* --border-2 */

  /* Text */
  --dfp-ink:              var(--dfp-ink-950);
  --dfp-text:             var(--dfp-ink-950);   /* --text */
  --dfp-text-muted:       var(--dfp-ink-600);   /* --text-2 */
  --dfp-text-subtle:      var(--dfp-ink-400);

  /* Accent — EL CTA PRINCIPAL ES TINTA, NO NARANJA (tokens.css §5).
   * Es la decisión que más se nota del sistema y la que más fácil
   * es deshacer sin querer: si un botón primario vuelve a salir
   * naranja, el naranja deja de significar nada. */
  --dfp-accent:           var(--dfp-ink-950);
  --dfp-accent-hover:     var(--dfp-ink-800);
  --dfp-accent-active:    var(--dfp-ink-900);
  --dfp-accent-soft:      rgba(18, 18, 20, 0.06);
  --dfp-accent-soft-2:    rgba(18, 18, 20, 0.10);
  --dfp-accent-border:    var(--dfp-ink-950);
  --dfp-accent-ring:      rgba(18, 18, 20, 0.14);
  --dfp-accent-text:      var(--dfp-ink-950);
  --dfp-on-accent:        #FFFFFF;

  /* Foco visible — naranja 600 (tokens.css §12: el 500 se queda en
   * 2,89:1 sobre papel y no llega al 3:1 que exige un indicador).
   * Antes era azul, y el motivo era que el acento fuese rojo: el
   * foco tenía que leerse distinto de la selección. Ahora que el
   * acento es tinta, el naranja cumple ese papel Y es de la casa. */
  --dfp-focus-ring:       var(--dfp-signal-600);
  --dfp-focus-ring-glow:  rgba(195, 99, 11, 0.28);

  /* ─── 4. SEMÁNTICOS (tokens.css §4) ───────────────────────── */
  --dfp-success:          #1E7A45;   /* --ok */
  --dfp-success-soft:     #EDF6F0;
  --dfp-success-text:     #1A6B3C;
  --dfp-warning:          #B4650B;   /* --warn */
  --dfp-warning-soft:     #FBF3E6;
  --dfp-warning-text:     #8E5008;
  --dfp-danger:           #B3261E;   /* --error */
  --dfp-danger-soft:      #FBEDEC;
  --dfp-danger-text:      #8F1E17;
  --dfp-success-border:   #C2DFCE;
  --dfp-warning-border:   #EEDCBA;
  --dfp-danger-border:    #F0C9C6;

  /* ─── 9. RADIOS (tokens.css §9: 6 / 10 / 16 / 22 / 28) ──────
   * Seis pasos contra los cinco del sistema; el sm y el lg
   * interpolan. Generosos, que es lo que hace que un panel denso
   * no parezca una hoja de cálculo. El EDITOR conserva su escala
   * diminuta propia (styles/tokens.ts): ahí hay controles de 24px
   * y un radio de 10 los convierte en pastillas. */
  --dfp-r-xs:             6px;
  --dfp-r-sm:             8px;
  --dfp-r-md:             10px;
  --dfp-r-lg:             14px;
  --dfp-r-xl:             18px;
  --dfp-r-2xl:            24px;

  --dfp-r-sm-handoff:     8px;
  --dfp-r-md-handoff:     12px;
  --dfp-r-lg-handoff:     20px;
  --dfp-r-xl-handoff:     28px;
  --dfp-r-pill:           980px;

  /* ─── Scrims ──────────────────────────────────────────────── */
  --dfp-scrim:           rgba(18, 18, 20, 0.45);
  --dfp-scrim-strong:    rgba(18, 18, 20, 0.60);

  /* ─── 10. ELEVACIÓN (tokens.css §10) ───────────────────────
   * Sombras amplias, difusas y MONOCROMAS. El offset negativo del
   * segundo plano es lo que las hace difusas de verdad en vez de
   * un halo pegado al borde. */
  --dfp-shadow-xs:        none;
  --dfp-shadow-sm:        0 1px 2px rgba(0,0,0,.04), 0 2px 6px rgba(0,0,0,.04);
  --dfp-shadow-md:        0 2px 8px rgba(0,0,0,.05), 0 8px 24px -8px rgba(0,0,0,.10);
  --dfp-shadow-lg:        0 4px 14px rgba(0,0,0,.06), 0 18px 48px -16px rgba(0,0,0,.16);
  --dfp-shadow-xl:        0 8px 24px rgba(0,0,0,.07), 0 30px 70px -24px rgba(0,0,0,.22);

  --dfp-sh-card:          0 1px 1px rgba(0,0,0,.03), 0 10px 34px -10px rgba(0,0,0,.10);
  --dfp-sh-pop:           0 4px 14px rgba(0,0,0,.06), 0 18px 48px -16px rgba(0,0,0,.16);
  --dfp-sh-paper:         0 8px 24px rgba(0,0,0,.07), 0 30px 70px -24px rgba(0,0,0,.22);

  /* ─── 6. CRISTAL (tokens.css §6) ──────────────────────────── */
  --dfp-glass-bg:        rgba(255,255,255,0.60);
  --dfp-glass-strong:    rgba(255,255,255,0.74);
  --dfp-glass-line:      rgba(255,255,255,0.65);
  --dfp-glass-blur:      saturate(180%) blur(22px);

  /* ─── 7. TIPOGRAFÍA (tokens.css §7) ────────────────────────
   * Geist para todo: display, texto y datos. Es geométrica,
   * neutra y de marca — no es la tipografía del sistema de ningún
   * fabricante. Inter y system-ui de reserva.
   *
   * La jerarquía se construye por TAMAÑO, PESO y TRACKING, nunca
   * por familia (§7 del sistema: --tracking-display -0.032em,
   * --tracking-title -0.026em, --tracking-body -0.005em; el
   * tracking se aprieta según crece el cuerpo).
   *
   * Deroga el «Manrope para todo» de ago-2026. El EMBED comercial
   * sigue con su pareja editorial (Instrument Serif + Manrope)
   * pinada dentro de .dfp-root / .bd-overlay, y el estudio 3D del
   * cliente pina Manrope en su propia página: injectFonts() carga
   * las tres familias igual. */
  --dfp-font-display:     "Geist", "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --dfp-font-sans:        "Geist", "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --dfp-font-mono:        "Geist Mono", ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
  --dfp-font-script:      "Dancing Script", "Caveat", cursive;

  /* Tracking — escala del sistema, disponible como token para que
   * los titulares no se aprieten «a ojo» en cada superficie. */
  --dfp-tracking-display: -0.032em;
  --dfp-tracking-title:   -0.026em;
  --dfp-tracking-body:    -0.005em;
  --dfp-tracking-eyebrow: 0.09em;

  /* ─── Layout — sin cambios ────────────────────────────── */
  --dfp-topbar-h:         56px;
  /* 224 -> 240: con la pastilla activa, la chapa de la tienda y las
   * chapas de recuento, el carril iba justo de ancho. */
  --dfp-sidebar-w:        240px;

  /* ─── 11. MOVIMIENTO ──────────────────────────────────────────
   * Las DURACIONES se quedan como estaban (80/150/250): el sistema
   * marca 180/250/450, pensado para una web con scroll, y en un
   * panel donde se pasa el ratón por cincuenta filas seguidas eso
   * se siente pastoso. Lo que sí se adopta es la CURVA. */
  --dfp-t-fast:          80ms;
  --dfp-t-base:          150ms;
  --dfp-t-slow:          250ms;
  --dfp-ease:            cubic-bezier(.22, .61, .36, 1);
  --dfp-ease-out:        cubic-bezier(.16, 1, .3, 1);
  --dfp-ease-spring:     cubic-bezier(0.34, 1.56, 0.64, 1);

  /* ─── Spacing scale (PERSONALIZER_REFACTOR_PLAN §2.1.1) ─────
   * Arranca en 2px para chips de paleta / hairlines; cubre el 99 %
   * de los gaps/paddings actuales en el inspector. Sustituye los
   * 4/6/8/10/12 hardcodeados en styles.ts. */
  --dfp-space-0:         0;
  --dfp-space-1:         2px;
  --dfp-space-2:         4px;
  --dfp-space-3:         6px;
  --dfp-space-4:         8px;
  --dfp-space-5:         12px;
  --dfp-space-6:         16px;
  --dfp-space-7:         24px;
  --dfp-space-8:         32px;

  /* ─── Control sizes (PERSONALIZER_REFACTOR_PLAN §2.1.2) ───── */
  --dfp-control-sm:      24px;
  --dfp-control-md:      28px;
  --dfp-control-lg:      32px;
  --dfp-control-xl:      40px;

  /* ─── Z-index scale (PERSONALIZER_REFACTOR_PLAN §2.1.3) ───── */
  --dfp-z-tabs:          10;
  --dfp-z-dropdown:      100;
  --dfp-z-tooltip:       200;
  --dfp-z-modal:         1000;
  --dfp-z-toast:         1100;

  /* ─── Tags — colores de etiquetas de plantillas (PLAN §2.1) ──
   * Únicos colores saturados permitidos en el chrome del admin
   * además de los status. */
  --dfp-tag-red:         #ef4444;
  --dfp-tag-orange:      #f97316;
  --dfp-tag-yellow:      #eab308;
  --dfp-tag-green:       #22c55e;
  --dfp-tag-blue:        #3b82f6;
  --dfp-tag-purple:      #a855f7;
  --dfp-tag-pink:        #ec4899;
  --dfp-tag-grey:        #94a3b8;
}

/* ─── iOS-zoom defense ──────────────────────────────────────────
 * iOS Safari forcibly zooms the page when a touch user focuses an
 * input, textarea or select whose computed font-size is below
 * 16px. The admin's dense inputs (11–14px) trigger this on every
 * tap, causing a viewport jolt that the user has to pinch-zoom out
 * of. Force ≥16px on touch devices (and narrow viewports for
 * testability) so the zoom never triggers. Desktop density is
 * preserved — the rule only fires under (pointer: coarse) (real
 * iPhone/iPad/Android) or (max-width: 768px) (narrow desktop +
 * mobile-preset preview emulators that don't expose pointer: coarse).
 *
 * Specificity: :not(#\\0) bumps the selector to ID level (1,0,1).
 * Plain (input, textarea, select) would only be (0,0,1) and lose
 * against the dozens of class-scoped rules with !important that
 * pin font-size below 16px (e.g. .dfp-prs-hex 12px, .dfp-v2-row
 * input 12.5px, .dfp-csteps-icon-input 14px). #\\0 is an ID escape
 * for U+FFFD that no element ever carries, so :not() always passes
 * — purely a specificity hack. */
@media (pointer: coarse), (max-width: 768px) {
  input:not(#\\0),
  textarea:not(#\\0),
  select:not(#\\0) {
    font-size: 16px !important;
  }
}

/* ─── Dark mode (PERSONALIZER_REFACTOR_PLAN §2.1.4) ──────────────
 * Activado vía [data-theme="dark"] en <html> o <body>. F0 solo
 * declara las variables: el toggle real llega en F7. Paleta
 * monocroma estilo Linear/Vercel dark (acento invertido a casi-
 * blanco). Los status colors se mantienen hasta F7 — refinarlos
 * para contraste WCAG AA dentro del dark mode es trabajo aparte. */
[data-theme="dark"] {
  /* Surfaces */
  --dfp-bg:              #0e0e10;
  --dfp-surface:         #131316;
  --dfp-surface-2:       #1a1a1d;
  --dfp-surface-3:       #232328;
  --dfp-surface-hover:   #1f1f23;

  /* Borders */
  --dfp-border:          #2a2a2e;
  --dfp-border-soft:     #232328;
  --dfp-border-strong:   #3a3a3f;

  /* Text */
  --dfp-ink:             #f4f4f5;
  --dfp-text:            #f4f4f5;
  --dfp-text-muted:      #a1a1aa;
  --dfp-text-subtle:     #71717a;

  /* Accent (invertido: casi-blanco) — sigue siendo monocromo. */
  --dfp-accent:          #f4f4f5;
  --dfp-accent-hover:    #e4e4e7;
  --dfp-accent-active:   #ffffff;
  --dfp-accent-soft:     #232328;
  --dfp-accent-soft-2:   #2a2a2e;
  --dfp-accent-border:   #3a3a3f;
  --dfp-accent-ring:     #3a82f7;
  --dfp-accent-text:     #f4f4f5;
  --dfp-on-accent:       #0a0a0a;
}
`;

/** Injects the design tokens stylesheet once per document. Safe to call
 *  many times: subsequent calls are no-ops. Returns true on first
 *  injection, false on subsequent calls. */
export function injectTokens(doc: Document = document): boolean {
  if (doc.getElementById(STYLE_ID)) return false;
  const style = doc.createElement('style');
  style.id = STYLE_ID;
  style.textContent = TOKENS_CSS;
  // Insert as the FIRST child of <head> so consumer stylesheets can
  // safely override individual vars without specificity tricks.
  doc.head.insertBefore(style, doc.head.firstChild);
  return true;
}
